/**
 * Places the buy card on the vertical centre of the viewport, then keeps it
 * there with position:sticky as the page scrolls.
 *
 * CSS can't measure an element against itself, and a flex-centred full-height
 * sticky box drifts as it goes from flow to stuck. Publishing exact pixel
 * values lets `top` and the initial offset match, so the card lands centred
 * and stays put without a jump.
 *
 * Deliberately not bound to scroll: these values only change on resize or when
 * the card's own content changes (variant swap, price reveal after login).
 *
 * IMPORTANT — this script must stay a *passive* observer of the header.
 * `--dimark-card-offset` feeds `margin-top`, so every write reflows the page.
 * The header collapses its nav band on scroll, the theme's sticky header
 * mirrors the new header height onto an in-flow spacer, and the browser then
 * re-anchors the scroll position. If we recompute (and rewrite margin-top)
 * whenever the header resizes, that reflow enlarges the scroll-anchor
 * correction, the header's scroll-direction logic reads the correction as a
 * scroll *up*, and the menu flaps open/closed off a single wheel notch.
 * So: never observe the header, and never write a value that hasn't changed.
 */
(() => {
  const sticky = document.querySelector('.dimark-buy-card-sticky');
  const card = sticky && sticky.querySelector('.dimark-buy-card');
  if (!sticky || !card) return;

  const desktopQuery = window.matchMedia('(min-width: 901px)');

  const headerOffset = () => {
    const header = document.querySelector('store-header.header');
    if (header) return header.getBoundingClientRect().height;

    const declared = getComputedStyle(document.documentElement).getPropertyValue('--header-end');
    return parseFloat(declared) || 96;
  };

  /**
   * Document-space top of the card's *natural* flow position.
   *
   * `getBoundingClientRect()` reports the stuck position once the card is
   * stuck, and Blink folds the sticky displacement into `offsetTop` too, so
   * both lie while scrolled. Suspending stickiness for the read gives the real
   * flow position at any scroll offset. Sticky boxes already occupy normal
   * flow space, so `position: static` changes nothing else about the layout.
   */
  const naturalTop = (currentMargin) => {
    const previous = sticky.style.position;
    sticky.style.position = 'static';
    const top = sticky.getBoundingClientRect().top + window.scrollY - currentMargin;
    if (previous) {
      sticky.style.position = previous;
    } else {
      sticky.style.removeProperty('position');
    }
    return top;
  };

  let frame = null;
  let lastTop = null;
  let lastOffset = null;
  let lastMax = null;

  /** Only touch the DOM when the value actually changed — this breaks loops. */
  const write = (name, value) => {
    if (sticky.style.getPropertyValue(name) === value) return;
    sticky.style.setProperty(name, value);
  };

  /** Internal scroll only when content exceeds the capped height. */
  const syncScrollable = () => {
    if (!desktopQuery.matches) {
      card.classList.remove('is-scrollable');
      return;
    }

    card.classList.toggle('is-scrollable', card.scrollHeight > card.clientHeight + 1);
  };

  const update = () => {
    frame = null;

    if (!desktopQuery.matches) {
      if (lastTop !== null || lastOffset !== null || lastMax !== null) {
        sticky.style.removeProperty('--dimark-sticky-top');
        sticky.style.removeProperty('--dimark-card-offset');
        sticky.style.removeProperty('--dimark-buy-card-max');
        lastTop = null;
        lastOffset = null;
        lastMax = null;
      }
      syncScrollable();
      return;
    }

    const headerH = headerOffset();
    const gap = 12;
    const bottomGap = window.matchMedia('(pointer: coarse)').matches ? 56 : 16;
    const available = Math.max(180, window.innerHeight - headerH - gap - bottomGap);
    const nextMax = Math.round(available);

    /* Cap height before measuring so landscape tablets never clip the card. */
    write('--dimark-buy-card-max', `${nextMax}px`);

    const cardH = card.getBoundingClientRect().height;
    const room = window.innerHeight - headerH;
    const shortViewport = window.innerHeight < 900;
    const stickyTop = (!shortViewport && cardH + gap * 2 + bottomGap <= room)
      ? headerH + (room - cardH) / 2
      : headerH + gap;

    const currentMargin = parseFloat(getComputedStyle(sticky).marginTop) || 0;
    const naturalDocY = naturalTop(currentMargin);
    const offset = Math.max(0, stickyTop - naturalDocY);

    const nextTop = Math.round(stickyTop);
    const nextOffset = Math.round(offset);

    // Nothing moved — return without writing, so no reflow, so no observer
    // callback, so no feedback into the header's scroll-direction logic.
    if (nextTop === lastTop && nextOffset === lastOffset && nextMax === lastMax) {
      syncScrollable();
      return;
    }

    lastTop = nextTop;
    lastOffset = nextOffset;
    lastMax = nextMax;

    write('--dimark-sticky-top', `${nextTop}px`);
    write('--dimark-card-offset', `${nextOffset}px`);
    syncScrollable();
  };

  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(update);
  };

  if ('ResizeObserver' in window) {
    // The card only. Observing `store-header.header` here is what turned a
    // scroll-driven header collapse into a reflow of this card, and from there
    // into the menu flap described above.
    const observer = new ResizeObserver(schedule);
    observer.observe(card);
  }

  window.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('on:variant:change', schedule);
  document.addEventListener('on:breakpoint-change', schedule);
  if (typeof desktopQuery.addEventListener === 'function') {
    desktopQuery.addEventListener('change', schedule);
  } else if (typeof desktopQuery.addListener === 'function') {
    desktopQuery.addListener(schedule);
  }

  update();

  // The header settles its own height shortly after load (the product menu
  // opens on a timer). Re-measure once after that, instead of subscribing to
  // every header resize for the life of the page.
  window.addEventListener('load', schedule);
  window.setTimeout(schedule, 700);
})();
